import React, { useState } from 'react';
import { Box, Chip, TextField, InputAdornment, FormControl, Select, MenuItem, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import AmendTransactionDialog from './AmendTransactionDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
import { useToast } from './ui/ToastProvider';
interface ScreeningInfo {
  engine: string;
  score: number;
  hits: string[];
  failReasonCode: string;
  failReasonText: string;
  screenedAt: string;
}
const MTO_NAMES = ['GTBank Ghana', 'Paga Ghana', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal'];
const AGENT_CODES = ['Ecobank Ghana', 'Ecobank Nigeria', 'Ecobank Senegal', 'Ecobank Uganda', 'Ecobank Kenya', "Ecobank Côte d'Ivoire", 'Ecobank Togo'];
const BRANCH_CODES = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
export interface ComplianceTransaction {
  txnId: string;
  mtoName: string;
  payoutType: 'FT' | 'IBFT' | 'OTC';
  beneficiaryName: string;
  beneficiaryCnic: string;
  beneficiaryTitle: string;
  amountFcy: string;
  currencyFcy: string;
  amountPkr: string;
  createdAt: string;
  lastUpdateAt: string;
  lifecycle: 'IN_REVIEW';
  currentState: 'SCREENING';
  mtoRef: string;
  bankRef: string;
  branchCode: string;
  agentCode: string;
  railRefs: {
    RaastMsgId?: string;
    RRN?: string;
    STAN?: string;
    UETR?: string;
  };
  screening: {
    engine: string;
    score: number;
    hits: string[];
    failReasonCode: string;
    failReasonText: string;
    screenedAt: string;
  };
  remitterName: string;
  remitterIdType: string;
  remitterIdNumber: string;
  remitterCountry: string;
  beneficiaryIban?: string;
  beneficiaryBank?: string;
  purposeCode: string;
  purposeText: string;
  fxRate: number;
}
interface ComplianceTableProps {
  transactions: ComplianceTransaction[];
  onRowClick: (transaction: ComplianceTransaction) => void;
  onExport: () => void;
}
const ComplianceTable: React.FC<ComplianceTableProps> = ({
  transactions,
  onRowClick,
  onExport
}) => {
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAmendTransaction, setSelectedAmendTransaction] = useState<ComplianceTransaction | null>(null);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState<ComplianceTransaction | null>(null);
  const {
    showToast
  } = useToast();
  const [filters, setFilters] = useState({
    txnId: '',
    mtoName: '',
    payoutType: '',
    beneficiaryName: '',
    beneficiaryCnic: '',
    beneficiaryTitle: '',
    branchCode: '',
    agentCode: [] as string[],
    amountFcyMin: '',
    amountFcyMax: '',
    amountPkrMin: '',
    amountPkrMax: '',
    dateFrom: '',
    dateTo: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  const handleAmendClick = (transaction: ComplianceTransaction) => {
    setSelectedAmendTransaction(transaction);
    setAmendDialogOpen(true);
  };
  const handleAmendConfirm = async (reason: string) => {
    if (!selectedAmendTransaction) return;
    showToast(`Amendment request submitted for ${selectedAmendTransaction.txnId}`, 'success');
    setAmendDialogOpen(false);
    setSelectedAmendTransaction(null);
  };
  const handleCancelClick = (transaction: ComplianceTransaction) => {
    setSelectedCancelTransaction(transaction);
    setCancelDialogOpen(true);
  };
  const handleCancelConfirm = async (reason: string) => {
    if (!selectedCancelTransaction) return;
    showToast(`Cancellation request submitted for ${selectedCancelTransaction.txnId}`, 'success');
    setCancelDialogOpen(false);
    setSelectedCancelTransaction(null);
  };
  // Define columns
  const columns: Column<ComplianceTransaction>[] = [{
    id: 'txnId',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" data-id="element-710">
          {row.txnId}
        </Typography>,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-711">
          <Select value={filters.txnId || 'all'} onChange={e => setFilters({
        ...filters,
        txnId: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-712">
            <MenuItem value="all" data-id="element-713">All</MenuItem>
            {Array.from(new Set(transactions.map(t => t.txnId))).map(id => <MenuItem key={id} value={id} data-id="element-714">
                {id}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'agentCode',
    label: 'Agent Code',
    renderCell: row => <Chip label={row.agentCode || ''} size="small" sx={{
      backgroundColor: 'rgba(79, 70, 229, 0.1)',
      color: '#4F46E5',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.agentCode ? 'visible' : 'hidden'
    }} data-id="element-715" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-716">
          <Select multiple value={filters.agentCode} onChange={e => setFilters({
        ...filters,
        agentCode: e.target.value as string[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-717">
            {AGENT_CODES.map(code => <MenuItem key={code} value={code} data-id="element-718">
                {code}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || ''} size="small" sx={{
      backgroundColor: 'rgba(6, 182, 212, 0.1)',
      color: '#06B6D4',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.branchCode ? 'visible' : 'hidden'
    }} data-id="element-719" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-721">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-722" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-720" />
  }, {
    id: 'mtoName',
    label: 'Remitting Institute',
    renderCell: row => <Typography variant="body2" fontWeight={500} data-id="element-723">
          {row.mtoName}
        </Typography>,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-724">
          <Select value={filters.mtoName || 'all'} onChange={e => setFilters({
        ...filters,
        mtoName: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-725">
            <MenuItem value="all" data-id="element-726">All</MenuItem>
            {Array.from(new Set(transactions.map(t => t.mtoName))).map(name => <MenuItem key={name} value={name} data-id="element-727">
                  {name}
                </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => {
      const colors = {
        FT: {
          bg: 'rgba(14, 165, 233, 0.1)',
          color: '#0EA5E9'
        },
        IBFT: {
          bg: 'rgba(139, 92, 246, 0.1)',
          color: '#8B5CF6'
        },
        OTC: {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        }
      };
      const style = colors[row.payoutType];
      return <Chip label={row.payoutType} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-728" />;
    },
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-729">
          <Select value={filters.payoutType || 'all'} onChange={e => setFilters({
        ...filters,
        payoutType: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-730">
            <MenuItem value="all" data-id="element-731">All</MenuItem>
            <MenuItem value="FT" data-id="element-732">FT</MenuItem>
            <MenuItem value="IBFT" data-id="element-733">IBFT</MenuItem>
            <MenuItem value="OTC" data-id="element-734">OTC</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary Name',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.beneficiaryName} onChange={e => setFilters({
      ...filters,
      beneficiaryName: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-736">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-737" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-735" />
  }, {
    id: 'beneficiaryCnic',
    label: 'Beneficiary CNIC',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" data-id="element-738">
          {row.beneficiaryCnic}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.beneficiaryCnic} onChange={e => setFilters({
      ...filters,
      beneficiaryCnic: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-740">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-741" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-739" />
  }, {
    id: 'beneficiaryTitle',
    label: 'Beneficiary Title',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" data-id="element-742">
          {row.beneficiaryTitle}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.beneficiaryTitle} onChange={e => setFilters({
      ...filters,
      beneficiaryTitle: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-744">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-745" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-743" />
  }, {
    id: 'amountFcy',
    label: 'Amount FCY',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-746">
          {row.currencyFcy} {row.amountFcy}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-747">
          <TextField size="small" placeholder="Min" value={filters.amountFcyMin} onChange={e => setFilters({
        ...filters,
        amountFcyMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-748" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-749" />
          <TextField size="small" placeholder="Max" value={filters.amountFcyMax} onChange={e => setFilters({
        ...filters,
        amountFcyMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-750" />
        </Box>
  }, {
    id: 'amountPkr',
    label: 'Local Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-751">
          {row.amountPkr}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-752">
          <TextField size="small" placeholder="Min" value={filters.amountPkrMin} onChange={e => setFilters({
        ...filters,
        amountPkrMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-753" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-754" />
          <TextField size="small" placeholder="Max" value={filters.amountPkrMax} onChange={e => setFilters({
        ...filters,
        amountPkrMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-755" />
        </Box>
  }, {
    id: 'createdAt',
    label: 'Created',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" data-id="element-756">
          {new Date(row.createdAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-757">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-758">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-759" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-760" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-761">
              {dateRangeError}
            </Typography>}
        </Box>
  }, {
    id: 'lastUpdateAt',
    label: 'Last Update',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" data-id="element-762">
          {new Date(row.lastUpdateAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      height: 36
    }} data-id="element-763" />
  }];
  // Apply filters
  const filteredTransactions = transactions.filter(txn => {
    if (filters.txnId && !txn.txnId.includes(filters.txnId)) return false;
    if (filters.mtoName && txn.mtoName !== filters.mtoName) return false;
    if (filters.payoutType && txn.payoutType !== filters.payoutType) return false;
    if (filters.beneficiaryName && !txn.beneficiaryName.toLowerCase().includes(filters.beneficiaryName.toLowerCase())) return false;
    if (filters.beneficiaryCnic && !txn.beneficiaryCnic.includes(filters.beneficiaryCnic)) return false;
    if (filters.beneficiaryTitle && !txn.beneficiaryTitle.includes(filters.beneficiaryTitle)) return false;
    // Agent Code filter
    if (filters.agentCode.length > 0) {
      if (!filters.agentCode.includes(txn.agentCode)) return false;
    }
    // Branch Code filter
    if (filters.branchCode && !txn.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase())) {
      return false;
    }
    // Amount FCY filter
    if (filters.amountFcyMin || filters.amountFcyMax) {
      const amount = parseFloat(txn.amountFcy);
      if (filters.amountFcyMin && amount < parseFloat(filters.amountFcyMin)) return false;
      if (filters.amountFcyMax && amount > parseFloat(filters.amountFcyMax)) return false;
    }
    // Amount PKR filter
    if (filters.amountPkrMin || filters.amountPkrMax) {
      const amount = parseFloat(txn.amountPkr.replace(/,/g, ''));
      if (filters.amountPkrMin && amount < parseFloat(filters.amountPkrMin)) return false;
      if (filters.amountPkrMax && amount > parseFloat(filters.amountPkrMax)) return false;
    }
    if (filters.dateFrom) {
      const txnDate = new Date(txn.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (txnDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const txnDate = new Date(txn.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (txnDate > toDate) return false;
    }
    return true;
  });
  const filterChips: FilterChip[] = [...(filters.txnId ? [{
    id: 'txnId',
    label: 'Txn ID',
    value: filters.txnId,
    onDelete: () => setFilters({
      ...filters,
      txnId: ''
    })
  }] : []), ...(filters.agentCode.length > 0 ? [{
    id: 'agentCode',
    label: 'Agent Code',
    value: filters.agentCode.join(', '),
    onDelete: () => setFilters({
      ...filters,
      agentCode: []
    })
  }] : []), ...(filters.branchCode ? [{
    id: 'branchCode',
    label: 'Branch Code',
    value: filters.branchCode,
    onDelete: () => setFilters({
      ...filters,
      branchCode: ''
    })
  }] : []), ...(filters.mtoName ? [{
    id: 'mtoName',
    label: 'MTO',
    value: filters.mtoName,
    onDelete: () => setFilters({
      ...filters,
      mtoName: ''
    })
  }] : []), ...(filters.payoutType ? [{
    id: 'payoutType',
    label: 'Type',
    value: filters.payoutType,
    onDelete: () => setFilters({
      ...filters,
      payoutType: ''
    })
  }] : []), ...(filters.beneficiaryName ? [{
    id: 'beneficiaryName',
    label: 'Beneficiary',
    value: filters.beneficiaryName,
    onDelete: () => setFilters({
      ...filters,
      beneficiaryName: ''
    })
  }] : []), ...(filters.amountFcyMin || filters.amountFcyMax ? [{
    id: 'amountFcy',
    label: 'Amount FCY',
    value: filters.amountFcyMin && filters.amountFcyMax ? `${filters.amountFcyMin} - ${filters.amountFcyMax}` : filters.amountFcyMin ? `≥ ${filters.amountFcyMin}` : `≤ ${filters.amountFcyMax}`,
    onDelete: () => setFilters({
      ...filters,
      amountFcyMin: '',
      amountFcyMax: ''
    })
  }] : []), ...(filters.amountPkrMin || filters.amountPkrMax ? [{
    id: 'amountPkr',
    label: 'Local Amount',
    value: filters.amountPkrMin && filters.amountPkrMax ? `${filters.amountPkrMin} - ${filters.amountPkrMax}` : filters.amountPkrMin ? `≥ ${filters.amountPkrMin}` : `≤ ${filters.amountPkrMax}`,
    onDelete: () => setFilters({
      ...filters,
      amountPkrMin: '',
      amountPkrMax: ''
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : [])];
  const rowActions: RowAction<ComplianceTransaction>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => onRowClick(row)
  }, {
    icon: 'lucide:edit',
    label: 'Amend',
    onClick: row => handleAmendClick(row),
    condition: (row: any) => row.amendAllowed === true && (!row.amendStatus || row.amendStatus === 'NONE' || row.amendStatus === 'REJECTED')
  }, {
    icon: 'lucide:x-circle',
    label: 'Cancel',
    onClick: row => handleCancelClick(row),
    condition: (row: any) => row.cancelAllowed === true && (!row.cancelStatus || row.cancelStatus === 'NONE' || row.cancelStatus === 'REJECTED')
  }];
  return <>
      <DataTable title={`Compliance Review (${filteredTransactions.length} Pending)`} data={filteredTransactions} columns={columns} rowActions={rowActions} keyField="txnId" filterChips={filterChips} onClearAllFilters={() => setFilters({
      txnId: '',
      mtoName: '',
      payoutType: '',
      beneficiaryName: '',
      beneficiaryCnic: '',
      beneficiaryTitle: '',
      branchCode: '',
      agentCode: [],
      amountFcyMin: '',
      amountFcyMax: '',
      amountPkrMin: '',
      amountPkrMax: '',
      dateFrom: '',
      dateTo: ''
    })} onExport={onExport} emptyStateMessage="No transactions awaiting screening" onRowClick={onRowClick} data-id="element-764" />

      {selectedAmendTransaction && <AmendTransactionDialog open={amendDialogOpen} onClose={() => {
      setAmendDialogOpen(false);
      setSelectedAmendTransaction(null);
    }} onConfirm={handleAmendConfirm} transactionId={selectedAmendTransaction.txnId} data-id="element-765" />}

      {selectedCancelTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedCancelTransaction(null);
    }} onConfirm={handleCancelConfirm} transactionId={selectedCancelTransaction.txnId} cancellationAction={(selectedCancelTransaction as any).cancellationAction || 'SIMPLE_CANCEL'} data-id="element-766" />}
    </>;
};
export default ComplianceTable;